import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Leaf, Menu, Search as SearchIcon, User, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ACCOUNT_LINKS,
  HOME_SECTIONS,
  MAIN_NAV,
  PAGE_LINKS,
  resolveNavItem,
  scrollToHash,
} from '../config/navigation';
import { useAuth } from '../context/AuthContext';
import * as catalogApi from '../api/endpoints/catalog';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { visibleCategories } from '../utils/categories';
import CartBadge from './CartBadge';
import WishlistBadge from './WishlistBadge';
import NavSearch from './NavSearch';
import DeliveryLocation from './DeliveryLocation';
import { focusRing, homeShell } from '../styles/ui';

const Navbar = () => {
  const [logoError, setLogoError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  // Categories are scoped to the delivery location, so the cached list is
  // tagged with the locationVersion it was loaded for and never reused for
  // another geography.
  const [categoryCache, setCategoryCache] = useState({ version: null, list: [] });
  const accountMenuRef = useRef(null);
  const categoryMenuRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { authStatus, logout } = useAuth();
  const { storefrontStatus, locationVersion, openLocationPrompt } = useDeliveryLocation();
  const storefrontReady = storefrontStatus === 'ready';
  const categories = categoryCache.version === locationVersion ? categoryCache.list : [];
  const onHome = pathname === '/';

  const handleLogoError = () => {
    setLogoError(true);
  };

  const closeMenu = () => setMenuOpen(false);

  // Escape closes the mobile menu, so keyboard users are never trapped in it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // Same Escape-to-close pattern as the mobile menu, applied to the account dropdown.
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    const onPointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [accountMenuOpen]);

  // ...and to the Categories dropdown.
  useEffect(() => {
    if (!categoryMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCategoryMenuOpen(false);
    };
    const onPointerDown = (event) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setCategoryMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [categoryMenuOpen]);

  // Same Escape-to-close pattern, applied to the header search bar.
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [searchOpen]);

  // The Categories dropdown lists the live admin taxonomy for the delivery
  // location, fetched only once the shopper opens it — the header must not pay
  // for a catalogue call on every page load — and again after the location
  // changes. A same-geography refresh keeps locationVersion, so no refetch.
  const categoriesLoadedForVersion = categoryCache.version === locationVersion;
  useEffect(() => {
    if (!categoryMenuOpen || !storefrontReady || categoriesLoadedForVersion) return undefined;
    let active = true;
    const requestedVersion = locationVersion;
    catalogApi
      .listCategories()
      .then((response) => {
        if (active) setCategoryCache({ version: requestedVersion, list: visibleCategories(response.data) });
      })
      .catch(() => {
        // Nothing cached; reopening the menu tries again.
      });
    return () => {
      active = false;
    };
  }, [categoryMenuOpen, storefrontReady, categoriesLoadedForVersion, locationVersion]);

  const handleLogout = async () => {
    setAccountMenuOpen(false);
    closeMenu();
    await logout();
    navigate('/');
  };

  // Section links are real anchors to `/#section`. On the homepage we intercept
  // and scroll smoothly (the existing behaviour). On any other route we let the
  // router navigate to `/#section`, and HomePage scrolls once it has mounted —
  // so we never hunt for homepage section ids from another page.
  const handleSectionClick = (event, hash) => {
    closeMenu();
    if (onHome) {
      event.preventDefault();
      scrollToHash(hash);
    }
  };

  const handleLogoClick = (event) => {
    closeMenu();
    if (onHome) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinkClass = `relative whitespace-nowrap text-[14px] 2xl:text-[15px] text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`;
  // The reference marks the current top-level destination with a filled pill
  // rather than an underline.
  const activePillClass =
    'inline-flex items-center h-10 px-5 rounded-full bg-gradient-primary text-white font-semibold text-[15px] shadow-glow-teal whitespace-nowrap';

  const mainNav = useMemo(
    () => MAIN_NAV.map((entry) => ({ entry, item: resolveNavItem(entry) })).filter((row) => row.item),
    []
  );

  const renderNavEntry = ({ entry, item }) => {
    if (entry.dropdown === 'categories') {
      return (
        <div key={item.to} ref={categoryMenuRef} className="relative">
          <button
            type="button"
            onClick={() => setCategoryMenuOpen((open) => !open)}
            aria-expanded={categoryMenuOpen}
            aria-haspopup="true"
            className={`inline-flex items-center gap-1 ${navLinkClass} ${
              pathname.startsWith('/categories') ? 'text-emerald-700 font-semibold' : ''
            }`}
          >
            {item.label}
            <ChevronDown size={15} aria-hidden="true" />
          </button>
          {categoryMenuOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 mt-3 w-60 bg-white rounded-2xl border border-emerald-100 shadow-floating py-2 z-50">
              {!storefrontReady && (
                <button
                  type="button"
                  onClick={() => {
                    setCategoryMenuOpen(false);
                    openLocationPrompt();
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm text-emerald-800 hover:bg-emerald-50 ${focusRing}`}
                >
                  {storefrontStatus === 'checking'
                    ? 'Checking availability for your location…'
                    : 'Select a delivery location to see what we stock'}
                </button>
              )}
              {storefrontReady && categories.map((category) => (
                <Link
                  key={category.key}
                  to={`/categories/${category.key}`}
                  onClick={() => setCategoryMenuOpen(false)}
                  className={`block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 ${focusRing}`}
                >
                  {category.label}
                </Link>
              ))}
              <Link
                to="/categories"
                onClick={() => setCategoryMenuOpen(false)}
                className={`block px-4 py-2.5 mt-1 border-t border-emerald-50 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 ${focusRing}`}
              >
                All categories
              </Link>
            </div>
          )}
        </div>
      );
    }

    if (entry.kind === 'page') {
      return (
        <Link
          key={item.to}
          to={item.to}
          onClick={closeMenu}
          aria-current={pathname === item.to ? 'page' : undefined}
          className={`${navLinkClass} ${pathname === item.to ? 'text-emerald-700 font-semibold' : ''}`}
        >
          {item.label}
        </Link>
      );
    }

    const active = onHome && entry.hash === '#home';
    return (
      <Link
        key={item.hash}
        to={`/${item.hash}`}
        onClick={(e) => handleSectionClick(e, item.hash)}
        className={active ? activePillClass : navLinkClass}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header className="fixed w-full z-50 top-0">
      <nav className="bg-white/95 backdrop-blur-xl border-b border-emerald-50 shadow-soft">
        <div className={homeShell}>
          <div className="flex items-center justify-between gap-4 h-20">
            {/* Logo Area — shrink-0 so flexbox never crushes the brand to zero
                width when the row is tight. */}
            <div className="flex items-center shrink-0" data-testid="navbar-logo-container">
              <Link
                to="/"
                onClick={handleLogoClick}
                className={`flex items-center ${focusRing}`}
                aria-label="Fresh & Homely — go to home page"
              >
                {!logoError ? (
                  <img
                    src="/fresh-homely-logo.png"
                    alt="Fresh & Homely"
                    className="h-11 sm:h-12 xl:h-14 w-auto object-contain"
                    onError={handleLogoError}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center text-white shadow-glow-teal">
                      <Leaf size={24} aria-hidden="true" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xl font-bold text-emerald-900 tracking-tight leading-none uppercase">
                        Fresh &amp;
                      </span>
                      <span className="text-sm font-medium text-emerald-600 tracking-widest uppercase">Homely</span>
                    </div>
                  </div>
                )}
              </Link>
            </div>

            <div className="hidden xl:flex flex-1 min-w-0 max-w-sm 2xl:max-w-md">
              <NavSearch variant="mobile" />
            </div>

            {/* Centred primary navigation. Below xl the same destinations stay
                reachable through the menu button. */}
            <div className="hidden xl:flex items-center gap-4 2xl:gap-6 shrink-0">{mainNav.map(renderNavEntry)}</div>

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <DeliveryLocation className="hidden lg:flex" />

              <button
                type="button"
                onClick={() => setSearchOpen((open) => !open)}
                className={`xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-emerald-900 bg-emerald-50/60 hover:bg-emerald-50 transition-colors ${focusRing}`}
                aria-label={searchOpen ? 'Close search' : 'Open search'}
                aria-expanded={searchOpen}
                aria-controls="header-search-bar"
              >
                {searchOpen ? <X size={20} aria-hidden="true" /> : <SearchIcon size={20} aria-hidden="true" />}
              </button>

              <WishlistBadge />
              <CartBadge />

              {authStatus === 'authenticated' ? (
                // Ref spans the trigger *and* the dropdown so the outside-click
                // handler treats both as "inside": on the trigger it would
                // otherwise close and immediately reopen, and on a menu item it
                // would unmount the link before its click handler ran.
                <div ref={accountMenuRef} className="relative hidden md:block">
                  <button
                    type="button"
                    onClick={() => setAccountMenuOpen((open) => !open)}
                    aria-label="Account menu"
                    aria-expanded={accountMenuOpen}
                    aria-controls="account-menu"
                    className={`inline-flex items-center justify-center w-11 h-11 rounded-full text-emerald-900 bg-emerald-50/60 hover:bg-emerald-50 transition-colors ${focusRing}`}
                  >
                    <User size={20} aria-hidden="true" />
                  </button>
                  {accountMenuOpen && (
                    <div
                      id="account-menu"
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl border border-emerald-100 shadow-lg py-2"
                    >
                      {ACCOUNT_LINKS.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setAccountMenuOpen(false)}
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 ${focusRing}`}
                        >
                          {item.label}
                        </Link>
                      ))}
                      <button
                        type="button"
                        onClick={handleLogout}
                        className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 ${focusRing}`}
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  aria-label="Login"
                  className={`hidden md:inline-flex items-center justify-center w-11 h-11 rounded-full text-emerald-900 bg-emerald-50/60 hover:bg-emerald-50 transition-colors ${focusRing}`}
                >
                  <User size={20} aria-hidden="true" />
                </Link>
              )}

              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className={`xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-emerald-900 hover:bg-emerald-50 transition-colors ${focusRing}`}
                aria-label={menuOpen ? 'Close main menu' : 'Open main menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
              >
                {menuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {searchOpen && (
            <div id="header-search-bar" className="xl:hidden pb-4 animate-fade-in" style={{ animationDuration: '200ms' }}>
              <div className="mx-auto max-w-2xl">
                <NavSearch variant="mobile" onRequestClose={() => setSearchOpen(false)} />
              </div>
            </div>
          )}
        </div>

        {menuOpen && (
          <div id="mobile-menu" className="xl:hidden border-t border-emerald-50 bg-white shadow-soft">
            <div className="mx-auto w-full max-w-[112rem] px-4 sm:px-6 py-4 flex flex-col">
              <DeliveryLocation className="lg:hidden mb-2 self-start" />
              {HOME_SECTIONS.map((item) => (
                <Link
                  key={item.hash}
                  to={`/${item.hash}`}
                  onClick={(e) => handleSectionClick(e, item.hash)}
                  className={`py-3 text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
                >
                  {item.label}
                </Link>
              ))}
              {PAGE_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  aria-current={pathname === item.to ? 'page' : undefined}
                  className={`py-3 font-medium transition-colors ${focusRing} ${
                    pathname === item.to ? 'text-emerald-700 font-semibold' : 'text-gray-700 hover:text-emerald-700'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              {authStatus === 'authenticated' ? (
                <>
                  {ACCOUNT_LINKS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={closeMenu}
                      className={`py-3 text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`py-3 text-left text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className={`py-3 text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
